import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Clock, TrendingDown, AlertCircle, Eye, CheckCircle, XCircle } from "lucide-react";
import { formatWithTimezoneLabel } from "@/lib/timezone";

interface ClosedAuction {
  id: string;
  rfp_id: string;
  initial_bid_amount: number;
  invitation_id: string;
  invite_token: string;
  selection_status: string;
  rfp: {
    title: string;
    description: string;
    auction_start: string;
    auction_end: string;
    status: string;
    matter_name: string | null;
  };
  finalBid: number;
  lowestBid: number;
}

const SupplierClosedAuctions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<ClosedAuction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && user) {
      fetchClosedAuctions();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchClosedAuctions = async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    try {
      // Get invitations for this user
      const { data: invitations } = await supabase
        .from("supplier_invitations")
        .select("id, invite_token, rfp_id")
        .eq("supplier_email", user.email);

      if (!invitations || invitations.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      const invitationIds = invitations.map(inv => inv.id);
      const rfpIds = invitations.map(inv => inv.rfp_id);

      // Get proposals for these invitations (including those with selection_status set)
      const { data: proposals } = await supabase
        .from("proposals")
        .select("id, rfp_id, initial_bid_amount, invitation_id, selection_status")
        .in("invitation_id", invitationIds);

      if (!proposals || proposals.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      // Get RFPs - include all statuses since we'll filter by selection_status too
      const { data: rfpData } = await supabase
        .from("rfps")
        .select("id, title, description, auction_start, auction_end, status, matter_name, deleted_by_buyer")
        .in("id", rfpIds);

      if (!rfpData || rfpData.length === 0) {
        setAuctions([]);
        setLoading(false);
        return;
      }

      // Filter to show auctions that are:
      // 1. Status is AUCTION_ENDED or COMPLETED, OR
      // 2. Proposal has selection_status of 'successful' or 'unsuccessful'
      // Exclude deleted RFPs
      const now = new Date();
      const closedRfps = rfpData.filter(rfp => {
        if (rfp.deleted_by_buyer) return false;
        
        const proposal = proposals.find(p => p.rfp_id === rfp.id);
        if (!proposal) return false;
        
        // Include if RFP status is ended/completed
        if (rfp.status === "AUCTION_ENDED" || rfp.status === "COMPLETED") {
          return true;
        }
        
        // Include if proposal has been selected (successful or unsuccessful)
        if (proposal.selection_status === "successful" || proposal.selection_status === "unsuccessful") {
          return true;
        }
        
        // Include if auction end time has passed
        const auctionEnd = new Date(rfp.auction_end);
        if (auctionEnd <= now) {
          return true;
        }
        
        return false;
      });

      // For each closed auction, get bid data
      const auctionData = await Promise.all(
        closedRfps.map(async (rfp) => {
          const proposal = proposals.find(p => p.rfp_id === rfp.id);
          const invitation = invitations.find(inv => inv.rfp_id === rfp.id);
          
          if (!proposal || !invitation) return null;

          // Get all bids for this RFP
          const { data: allBids } = await supabase
            .from("bids")
            .select("amount")
            .eq("rfp_id", rfp.id)
            .order("amount", { ascending: true });

          // Get my bids
          const { data: myBids } = await supabase
            .from("bids")
            .select("amount")
            .eq("proposal_id", proposal.id)
            .order("amount", { ascending: true });

          const lowestBid = allBids && allBids.length > 0 ? allBids[0].amount : proposal.initial_bid_amount;
          const finalBid = myBids && myBids.length > 0 ? myBids[0].amount : proposal.initial_bid_amount;

          return {
            id: proposal.id,
            rfp_id: rfp.id,
            initial_bid_amount: proposal.initial_bid_amount,
            invitation_id: proposal.invitation_id,
            invite_token: invitation.invite_token,
            selection_status: proposal.selection_status,
            rfp: rfp,
            finalBid,
            lowestBid
          };
        })
      );

      setAuctions(auctionData.filter(Boolean) as ClosedAuction[]);
    } catch (err) {
      console.error("Error fetching closed auctions:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (selectionStatus: string) => {
    if (selectionStatus === "successful") {
      return (
        <Badge className="bg-green-500 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Awarded
        </Badge>
      );
    }
    if (selectionStatus === "unsuccessful") {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Unsuccessful
        </Badge>
      );
    }
    return <Badge variant="secondary">Pending Decision</Badge>;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Closed Auctions</h1>
          <p className="text-muted-foreground mt-2">Auctions that have ended</p>
        </div>

        {auctions.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No closed auctions</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {auctions.map((auction) => (
              <Card key={auction.id} className="hover:shadow-lg transition-shadow flex flex-col">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {auction.rfp.matter_name || auction.rfp.title}
                    </CardTitle>
                    {getStatusBadge(auction.selection_status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
                    {auction.rfp.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-xs text-blue-600">Your Final Bid</p>
                      <p className="text-lg font-bold text-blue-700">${auction.finalBid.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-xs text-green-600">Winning Bid</p>
                      <p className="text-lg font-bold text-green-700">${auction.lowestBid.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Ended: {formatWithTimezoneLabel(auction.rfp.auction_end)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingDown className="h-4 w-4" />
                      <span>Savings: {((1 - auction.finalBid / auction.initial_bid_amount) * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="mt-auto pt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/supplier/rfp/${auction.rfp_id}/view/${auction.invite_token}`)}
                      className="w-full"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierClosedAuctions;
